import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import DefaultModal from 'components/Utils/DefaultModal';
import Graph from 'components/HealthRecords/Graph';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import DefaultChip from 'components/Utils/DefaultChip';
import './style.scss';
import { useAppSelector } from 'careand-redux/store';
import CPPUtils from 'services/CPPUtils';
import CalendarUtils from 'services/CalendarUtils';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { useAppDispatch } from 'careand-redux/useAppDispatch';

const DEFAULT_ARRAY = [];

const CPPListMetrics = (props) => {
  const dispatch = useAppDispatch();

  const [openGraph, setOpenGraph] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState();

  const patientId = props.patient.id;

  const patientHealthRecords =
    useAppSelector((state) => state.healthRecords.byId[patientId]?.dataSummary) || DEFAULT_ARRAY;

  const patientSummary = useMemo(() => {
    return CPPUtils.orderMetrics(patientHealthRecords).filter((summary) => summary.metric?.id);
  }, [patientHealthRecords]);

  function onClick(metric, type, origin) {
    if (metric) {
      let value = metric.id;
      if (type) value += `/${type}`;
      value += `_${origin}`;
      setSelectedMetric(value);
    }
    setOpenGraph(true);
  }

  function onClose() {
    setOpenGraph(false);
    setSelectedMetric();
  }

  return (
    <div className="cpp-small-box list-metrics collapse-list">
      {openGraph && (
        <DefaultModal
          className="graph-modal large-modal"
          title="Metrics"
          content={<Graph patient={props.patient} defaultMetric={selectedMetric} />}
          isOpen={openGraph}
          onRequestClose={onClose}
          wrap={true}
          padding={false}
          onPin={() => {
            dispatch(
              uiActions.addPinnedDocument({
                id: `metrics-${props.patient.id}`,
                type: 'metrics',
                patientId: props.patient.id,
                metric: selectedMetric,
              }),
            );
            onClose();
          }}
        />
      )}
      <div className="top-info row clickable">
        <div onClick={() => onClick()} style={{ display: 'contents' }}>
          <Icon src="chart-line" width="20px" height="20px" />
          <span className="small-title uppercase">Metrics</span>
          <div>
            <DefaultChip text={`${patientSummary.length}`} style={{ marginLeft: '10px' }} />
          </div>
        </div>
        <div className="list-content">
          {patientSummary.length > 0 && (
            <UncontrolledCollapse initialValue={true}>
              <div className="list">
                {patientSummary.map((summary) => {
                  const id = summary.metric.id + summary.type + summary.origin;
                  return (
                    <React.Fragment key={id}>
                      <Tooltip id={id} summary={summary} />
                      <div
                        className="tooltip-wrapper list-element truncate clickable"
                        onClick={() => onClick(summary.metric, summary.type, summary.origin)}
                        id={id}
                      >
                        <Icon src={CPPUtils.getMetricOriginSrc(summary.origin)} width="10px" />
                        &nbsp;
                        {summary.metric.name} {summary.type ? `(${summary.type})` : null}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </UncontrolledCollapse>
          )}
        </div>
      </div>
    </div>
  );
};

const Tooltip = ({ id, summary = {} }) => {
  let { last_value = {}, metric = {} } = summary;
  let { value1, value2, date, type } = last_value;

  let lastValue;
  let lastDate;
  if (value1) lastValue = value1;
  if (value2) lastValue += `/${value2}`;
  if (date) lastDate = CalendarUtils.format(date, 'YYYY-MM-DD');

  let lastValueText = lastValue;
  if (metric.units) lastValueText += ` ${metric.units}`;
  lastValueText += ` (${lastDate})`;

  return (
    <ReactTooltip anchorId={id} place="right" className="careand-tooltip" positionStrategy="fixed">
      <div>
        <span className="small-title">
          {metric.name} {type ? `(${type})` : ''}
        </span>
        <span>{lastValueText}</span>
        <span>Origin: {summary.origin || 'N/A'}</span>
      </div>
    </ReactTooltip>
  );
};

CPPListMetrics.propTypes = {
  patient: PropTypes.object.isRequired,
};

export default CPPListMetrics;
